import React, { useMemo, useState } from "react";
import {
  Modal,
  Form as AntDForm,
  Input,
  Select,
  message,
  Row,
  Col,
} from "antd";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { svg } from "../../../../Assets/svg";
import {
  ProductDropEnum,
  ProductDropLabels,
  ProductFinishEnum,
  ProductFinishLabels,
  formatFinishLabel,
  getDropOptions,
} from "../productsTypes";
import { BtnLoader } from "../../../../Components/Components";
import { useAppDispatch, useAppSelector, RootState } from "../../../../store";
import { updateVariantAPI } from "../productApis";
import { createVariantAPI } from "../../PriceSheet/priceSheetApis";

interface EditVariantModalProps {
  visible: boolean;
  onClose: () => void;
  isEditData: any; // null for add mode
  productId: string;
  productData: any;
  activeAdditionalFields: string[];
  categoriesOptions: { label: string; value: string }[];
  machinesOptions: { label: string; value: string }[];
  framePartOptions: {
    width: string[];
    height: string[];
    reinforced_height: string[];
    bolts_per_footplate: string[];
    depth: string[];
    thickness: string[];
    length: string[];
    connecting_points: string[];
    holes_drilled: string[];
  };
  onSuccess: (updatedVariant: any, isNew: boolean) => void;
}

const EditVariantModal: React.FC<EditVariantModalProps> = ({
  visible,
  onClose,
  isEditData,
  productId,
  productData,
  activeAdditionalFields,
  categoriesOptions,
  machinesOptions,
  framePartOptions,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const [isEditLoading, setIsEditLoading] = useState(false);

  const isAddMode =
    !isEditData?._id || isEditData?._id?.toString().startsWith("NEW_");

  const validationSchema = Yup.object().shape({
    product_code: Yup.string().trim().required("Product code is required"),
    variant_name: Yup.string().trim().required("Variant title is required"),
    coil_meterage_used: Yup.number()
      .min(0, "Must be a positive number")
      .nullable(),
    // categories: Yup.array().min(1, "At least one category is required"),
    // machines: Yup.array().min(1, "At least one machine is required"),
  });

  const initialValues = {
    product_code: isEditData?.product_code ?? "",
    variant_name: isEditData?.variant_name ?? "",
    height: isEditData?.height ?? null,
    reinforced_height: isEditData?.reinforced_height ?? null,
    width: isEditData?.width ?? null,
    depth: isEditData?.depth ?? null,
    length: isEditData?.length ?? null,
    thickness: isEditData?.thickness ?? null,
    finish: isEditData?.finish ?? null,
    color: isEditData?.color ?? "",
    secondary_finish: isEditData?.secondary_finish ?? null,
    secondary_color: isEditData?.secondary_color ?? "",
    bolts_per_footplate: isEditData?.bolts_per_footplate ?? null,
    dividers_per_shelf: isEditData?.dividers_per_shelf ?? null,
    connecting_points: isEditData?.connecting_points ?? null,
    holes_drilled: isEditData?.holes_drilled ?? null,
    first_beam_level: isEditData?.first_beam_level ?? "",
    frame_type: isEditData?.frame_type ?? "",
    foot_type: isEditData?.foot_type ?? "",
    shelf_reinforcers: isEditData?.shelf_reinforcers ?? "",
    crossties: isEditData?.crossties ?? "",
    pallet_supports: isEditData?.pallet_supports ?? "",
    chipboard_supports: isEditData?.chipboard_supports ?? "",
    v_type: isEditData?.v_type ?? "",
    drop: isEditData?.drop ?? null,
    custom_drop: isEditData?.custom_drop ?? null,
    gauge: isEditData?.gauge ?? null,
    level_width: isEditData?.level_width ?? null,
    level_depth: isEditData?.level_depth ?? null,
    first_shelf_level: isEditData?.first_shelf_level ?? null,
    max_load_per_bay: isEditData?.max_load_per_bay ?? null,
    max_load_per_shelf: isEditData?.max_load_per_shelf ?? null,
    max_load_per_pair: isEditData?.max_load_per_pair ?? null,
    coil_id: isEditData?.coil_id?.code ?? isEditData?.coil_id ?? "",
    coil_meterage_used: isEditData?.coil_meterage_used ?? null,

    weight: isEditData?.weight ?? null,

    categories: isEditData?.categories?.map((c: any) => c._id ?? c) ?? [],
    machines: isEditData?.machines?.map((m: any) => m._id ?? m) ?? [],
  };

  const dynamicFinishOptions = useMemo(() => {
    const availableFinishes = productData?.variant_columns?.finish || [];
    return availableFinishes.map((f: string) => ({
      value: f,
      label:
        ProductFinishLabels[f as ProductFinishEnum] || formatFinishLabel(f),
    }));
  }, [productData]);

  const dynamicSecondaryFinishOptions = useMemo(() => {
    const availableFinishes =
      productData?.variant_columns?.secondary_finish || [];
    return availableFinishes.map((f: string) => ({
      value: f,
      label:
        ProductFinishLabels[f as ProductFinishEnum] || formatFinishLabel(f),
    }));
  }, [productData]);

  const dynamicDropOptions = useMemo(() => {
    const availableDrops = productData?.variant_columns?.drop || [];
    return availableDrops.map((d: string) => ({
      value: d,
      label: ProductDropLabels[d as ProductDropEnum] || d,
    }));
  }, [productData]);

  const formatOptions = (arr: (string | number)[]) =>
    arr?.map((item) => ({ label: String(item), value: item })) || [];

  const handleSubmit = (values: any) => {
    setIsEditLoading(true);
    const payload = {
      ...values,
      product_id: productId,
    };

    const isCustomDrop =
      payload.drop === "custom" || payload.drop === ProductDropEnum.CUSTOM;

    if (!isCustomDrop) {
      payload.custom_drop = null;
    }

    // Sanitize numeric fields
    const numericFields = [
      "width",
      "height",
      "reinforced_height",
      "depth",
      "length",
      "thickness",
      "weight",
      "gauge",
      "level_width",
      "level_depth",
      "first_shelf_level",
      "custom_drop",
      "max_load_per_bay",
      "max_load_per_shelf",
      "max_load_per_pair",
      "coil_meterage_used",
    ];

    numericFields.forEach((field) => {
      const val = payload[field];
      if (val !== undefined && val !== null && val !== "") {
        payload[field] = Number(val);
      } else {
        if (payload.hasOwnProperty(field)) payload[field] = null;
      }
    });

    // Filter out irrelevant fields
    const allCheckFields = [
      "max_load_per_bay",
      "max_load_per_pair",
      "max_load_per_shelf",
      "width",
      "height",
      "reinforced_height",
      "depth",
      "length",
      "thickness",
      "bolts_per_footplate",
      "dividers_per_shelf",
      "finish",
      "connecting_points",
      "holes_drilled",
      "first_beam_level",
      "frame_type",
      "foot_type",
      "shelf_reinforcers",
      "crossties",
      "pallet_supports",
      "chipboard_supports",
      "v_type",
      "drop",
      "custom_drop",
      "gauge",
      "level_width",
      "level_depth",
      "first_shelf_level",
      "coil_id",
      "coil_meterage_used",
    ];

    allCheckFields.forEach((field) => {
      const isAdditional = [
        "max_load_per_bay",
        "max_load_per_pair",
        "max_load_per_shelf",
      ].includes(field);
      let isFieldVisible = true;
      if (isAdditional) {
        isFieldVisible =
          activeAdditionalFields.includes(field) ||
          (isEditData && field in isEditData);
      } else if (field === "coil_id" || field === "coil_meterage_used") {
        isFieldVisible = productData?.coil_meterage?.is_active === true;
      } else {
        isFieldVisible = productData?.variables?.[field] === true;
      }

      if (!isFieldVisible) {
        delete payload[field];
      }
    });

    if (isAddMode) {
      dispatch(
        createVariantAPI({
          payload,
          onSuccess: (data: any) => {
            onSuccess(data?.variant || data, true);
            onClose();
            setIsEditLoading(false);
          },
          onError: () => {
            setIsEditLoading(false);
          },
        }),
      );
    } else {
      dispatch(
        updateVariantAPI({
          variant_id: isEditData._id,
          payload,
          onSuccess: (data: any) => {
            onSuccess(data?.variant || data, false);
            onClose();
            setIsEditLoading(false);
          },
          onError: () => {
            setIsEditLoading(false);
          },
        }),
      );
    }
  };

  const renderField = (
    name: string,
    label: string,
    type: string = "text",
    options?: any,
    placeholder?: any,
    disabled?: boolean,
  ) => {
    const isVisible = (key: string) => {
      const dynamicKeys = [
        "width",
        "height",
        "reinforced_height",
        "depth",
        "length",
        "thickness",
        "bolts_per_footplate",
        "dividers_per_shelf",
        "finish",
        "secondary_finish",
        "connecting_points",
        "holes_drilled",
        "first_beam_level",
        "frame_type",
        "foot_type",
        "shelf_reinforcers",
        "crossties",
        "pallet_supports",
        "chipboard_supports",
        "v_type",
        "drop",
        "custom_drop",
        "gauge",
        "level_width",
        "level_depth",
        "first_shelf_level",
      ];

      if (key === "coil_id" || key === "coil_meterage_used") {
        return productData?.coil_meterage?.is_active === true;
      }

      if (dynamicKeys.includes(key)) {
        return productData?.variables?.[key] === true;
      }

      // ✅ NEW: Logic for Additional Fields (Max Load per Bay/Pair/Shelf)
      const additionalFields = [
        "max_load_per_bay",
        "max_load_per_pair",
        "max_load_per_shelf",
      ];
      if (additionalFields.includes(key)) {
        return (
          activeAdditionalFields.includes(key) || (isEditData && key in isEditData)
        );
      }

      return true;
    };

    if (
      !isVisible(name) &&
      name !== "color" &&
      name !== "secondary_color"
    )
      return null;

    if (name === "custom_drop") {
      const hasCustomDrop = productData?.variant_columns?.drop?.some(
        (f: string) => f === "custom" || f === ProductDropEnum.CUSTOM,
      );
      if (!hasCustomDrop) return null;
    }

    // Special handling for Color
    if (name === "color") {
      const hasPowderCoated = productData?.variant_columns?.finish?.some(
        (f: string) =>
          f.includes("powder") || f === ProductFinishEnum.POWDER_COATED,
      );
      if (!hasPowderCoated) return null;
    }

    // Special handling for Secondary Color
    if (name === "secondary_color") {
      const hasPowderCoated =
        productData?.variant_columns?.secondary_finish?.some(
          (f: string) =>
            f.includes("powder") || f === ProductFinishEnum.POWDER_COATED,
        );
      if (!hasPowderCoated) return null;
    }

    return (
      <Col md={12} span={24}>
        <div className="form-item">
          <label className="custom-label mb-1 ff">{label}</label>
          <Field name={name}>
            {({ field, form }: any) => {
              if (type === "select" || type === "multi-select") {
                return (
                  <Select
                    {...field}
                    mode={type === "multi-select" ? "multiple" : undefined}
                    className="w-100"
                    placeholder={placeholder || `Select ${label}`}
                    onChange={(val) => form.setFieldValue(name, val)}
                    suffixIcon={svg?.selectDownArrow}
                    disabled={disabled}
                  >
                    {options?.map((opt: any) => (
                      <Select.Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Select.Option>
                    ))}
                  </Select>
                );
              }
              return (
                <Input
                  {...field}
                  type={type}
                  placeholder={placeholder || `Enter ${label}`}
                  className="form-control form-control-lg"
                  disabled={disabled}
                />
              );
            }}
          </Field>
          <ErrorMessage
            name={name}
            component="div"
            className="text-danger mt-1 fs-12"
          />
        </div>
      </Col>
    );
  };

  return (
    <Modal
      title={
        <div className="text-center fs-4 fw-bolder">
          {isAddMode ? "Add Variant" : "Update Variant"}
        </div>
      }
      visible={visible}
      onCancel={onClose}
      width={"60rem"}
      closeIcon={svg?.closeIcon}
      centered
      footer={
        <div className="d-flex justify-content-center gap-3">
          <button
            type="button"
            className="btn btn-outline-secondary px-5"
            onClick={onClose}
            disabled={isEditLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary px-5"
            disabled={isEditLoading}
            onClick={() => {
              document.querySelector('.page-form')?.dispatchEvent(new Event('submit', { bubbles: true }));
            }}
          >
            {isEditLoading ? <BtnLoader /> : isAddMode ? "ADD" : "UPDATE"}
          </button>
        </div>
      }
      className="custom-modal"
      destroyOnClose
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue, handleSubmit: formikSubmit }) => (
          <Form onSubmit={formikSubmit} className="page-form">
            <Row gutter={[20, 20]}>
              {renderField("product_code", "Product Code", "text")}
              {renderField("variant_name", "Variant Title", "text")}
              {renderField(
                "height",
                "Height (mm)",
                "select",
                formatOptions(framePartOptions.height),
                null,
                true,
              )}
              {renderField(
                "reinforced_height",
                "Reinforced Height (mm)",
                "select",
                formatOptions(framePartOptions.reinforced_height),
                null,
                true,
              )}
              {renderField(
                "width",
                "Width (mm)",
                "select",
                formatOptions(framePartOptions.width),
                null,
                true,
              )}
              {renderField(
                "depth",
                "Depth (mm)",
                "select",
                formatOptions(framePartOptions.depth),
                null,
                true,
              )}
              {renderField(
                "length",
                "Length (mm)",
                "select",
                formatOptions(framePartOptions.length),
                null,
                true,
              )}
              {renderField(
                "thickness",
                "Thickness (mm)",
                "select",
                formatOptions(framePartOptions.thickness),
                null,
                true,
              )}
              {renderField("gauge", "Gauge (mm)", "number", null, null, true)}
              {renderField("level_width", "Level Width (mm)", "number", null, null, true)}
              {renderField("level_depth", "Level Depth (mm)", "number", null, null, true)}
              {renderField(
                "bolts_per_footplate",
                "Bolts per Footplate",
                "select",
                formatOptions(framePartOptions.bolts_per_footplate),
                null,
                true,
              )}
              {renderField(
                "dividers_per_shelf",
                "Dividers per Shelf",
                "text",
                null,
                null,
                true,
              )}
              {renderField(
                "connecting_points",
                "Connecting Points",
                "select",
                formatOptions(framePartOptions.connecting_points),
                null,
                true,
              )}
              {renderField(
                "holes_drilled",
                "Holes Drilled",
                "select",
                formatOptions(framePartOptions.holes_drilled),
                null,
                true,
              )}
              {renderField(
                "first_beam_level",
                "1st Beam Level (mm)",
                "text",
                null,
                null,
                true,
              )}
              {renderField(
                "frame_type",
                "Frame Type",
                "text",
                null,
                null,
                true,
              )}
              {renderField("foot_type", "Foot Type", "text", null, null, true)}
              {renderField(
                "shelf_reinforcers",
                "Shelf Reinforcers",
                "text",
                null,
                null,
                true,
              )}
              {renderField("crossties", "Crossties", "text", null, null, true)}
              {renderField(
                "pallet_supports",
                "Pallet Supports",
                "text",
                null,
                null,
                true,
              )}
              {renderField(
                "chipboard_supports",
                "Chipboard Supports",
                "text",
                null,
                null,
                true,
              )}
              {renderField("v_type", "Type", "text", null, null, true)}
              {renderField(
                "drop",
                "Drop",
                "select",
                dynamicDropOptions,
                null,
                true,
              )}
              {renderField(
                "custom_drop",
                "Custom Drop (mm)",
                "number",
                null,
                null,
                true,
              )}
              {renderField(
                "first_shelf_level",
                "1st Shelf Level (mm)",
                "number",
                null,
                null,
                true,
              )}
              {renderField(
                "finish",
                productData?.finish_label &&
                  productData?.variables?.secondary_finish
                  ? `${productData.finish_label} Finish`
                  : "Finish",
                "select",
                dynamicFinishOptions,
                null,
                true,
              )}
              {renderField(
                "color",
                "Color",
                "text",
                null,
                null,
                true,
              )}
              {renderField(
                "secondary_finish",
                productData?.secondary_finish_label
                  ? `${productData.secondary_finish_label} Finish`
                  : "Secondary Finish",
                "select",
                dynamicSecondaryFinishOptions,
                null,
                true,
              )}
              {renderField(
                "secondary_color",
                "Color",
                "text",
                null,
                null,
                true,
              )}

              {renderField(
                "max_load_per_bay",
                "Max Load per Bay",
                "number",
                null,
                null,
                false,
              )}

              {renderField(
                "max_load_per_pair",
                "Max Load per Pair",
                "number",
                null,
                null,
                false,
              )}

              {renderField(
                "max_load_per_shelf",
                "Max Load per Shelf",
                "number",
                null,
                null,
                false,
              )}

              {renderField("coil_id", "Coil ID", "text")}
              {renderField("coil_meterage_used", "Coil Metreage (m)", "number")}

              {renderField("weight", "Weight (kg)", "number")}
              {/* {renderField(
                "categories",
                "Categories",
                "multi-select",
                categoriesOptions,
              )} */}
              {/* {renderField(
                "machines",
                "Machines",
                "multi-select",
                machinesOptions,
              )} */}
            </Row>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default EditVariantModal;
